import { Box, Button, Group, Modal, Stack, Text, TextInput, Title, Checkbox } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useCallback, useState, type ChangeEvent } from "react";
import { getConviteByEmail } from "@/modules/convites/firestore";
import { extractTextInputPropertiesFromFormSchema } from "@/utils/form";
import useConviteForm, { ConviteForm } from "../hooks/useConviteForm";
import useCreateConvite from "../hooks/useCreateConvite";

type Properties = {
  licenciadaId?: TLicenciada["id"];
  empresaId: TEmpresa["id"];
  questionario: TQuestionario;
  empresaRespondenteId: TEmpresa["id"];
  opened: boolean;
  onClose: (convite: TConvite) => void;
  submitLabel: "Iniciar" | "Finalizar";
};

/**
 * @param properties - Properties
 * @returns React.FC
 */
function CreateConviteModal(properties: Properties) {
  const { form, descriptor } = useConviteForm();
  const acao = properties.questionario.autenticaInicio ? "responder" : "finalizar";
  const [receberRelatorioSimples, setReceberRelatorioSimples] = useState(false);

  const { createConvite } = useCreateConvite(properties);
  const getConvite = useCallback(
    (email: string) => {
      return getConviteByEmail(
        properties.empresaId,
        properties.questionario.id,
        properties.empresaRespondenteId,
        email
      );
    },
    [properties]
  );

  const saveReportPreferences = useCallback(
    (email: string) => {
      if (receberRelatorioSimples) {
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("Armazenando preferência de relatório simples no localStorage");
        }
        localStorage.setItem("receberRelatorioSimples", "true");
        localStorage.setItem("emailRelatorioSimples", email);
      } else {
        localStorage.removeItem("receberRelatorioSimples");
        localStorage.removeItem("emailRelatorioSimples");
      }
    },
    [receberRelatorioSimples]
  );

  const handleExistingConvite = useCallback(
    (conviteFound: TConvite, email: string) => {
      saveReportPreferences(email);
      properties.onClose(conviteFound);
    },
    [saveReportPreferences, properties]
  );

  const handleNewConvite = useCallback(
    async (values: ConviteForm) => {
      try {
        const newConvite = await createConvite(values.name, values.email);

        if (!newConvite) {
          throw new Error("Falha ao criar convite");
        }

        localStorage.setItem("emailConvite", values.email);
        saveReportPreferences(values.email);
        properties.onClose(newConvite);
      } catch (error) {
        console.error(error);
        const { message } = error as Error;
        showNotification({ message, color: "red" });
      }
    },
    [createConvite, saveReportPreferences, properties]
  );

  const handleSubmit = useCallback(
    (values: ConviteForm) => {
      const submit = async () => {
        const convite = await getConvite(values.email);
        const conviteFound = convite?.data() ?? null;

        if (conviteFound) {
          handleExistingConvite(conviteFound, values.email);
        } else {
          await handleNewConvite(values);
        }
      };

      void submit();
    },
    [getConvite, handleExistingConvite, handleNewConvite]
  );

  const handleModalClose = useCallback(() => {}, []);

  const handleRelatorioChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setReceberRelatorioSimples(event.currentTarget.checked);
  }, []);

  return (
    <Modal
      // prettier-ignore
      onClose={handleModalClose}
      opened={properties.opened}
      closeOnClickOutside={false}
      withCloseButton={false}
      closeOnEscape={false}
      size="lg"
      centered
    >
      <Box>
        <Stack>
          <Title>Prêmio Empresa Inovadora 2025</Title>
          <Text>Para {acao} o questionário, insira seu nome e e-mail no campo abaixo.</Text>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <TextInput
                {...form.getInputProps("name")}
                {...extractTextInputPropertiesFromFormSchema(descriptor, "name")}
              />
              <TextInput
                {...form.getInputProps("email")}
                {...extractTextInputPropertiesFromFormSchema(descriptor, "email")}
              />
              {properties.questionario.enviarRetornoEmail ? (
                <Checkbox
                  size="sm"
                  color="blue"
                  label="Desejo receber relatório simples de autoavaliação por e-mail"
                  checked={receberRelatorioSimples}
                  onChange={handleRelatorioChange}
                />
              ) : null}
              <Group justify="end" align="center">
                <Button type="submit">{properties.submitLabel}</Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Box>
    </Modal>
  );
}

export default CreateConviteModal;
